import React, { useState, useEffect } from "react"
import DataTable from "aldoo-ra/DataTable"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import Modal from "aldoo-ra/Modal"
import ContentEditor from "aldoo-ra/CMS/content-editor"
import { showNotification } from "aldoo-ra/Notification"
import { PlusCircleIcon, TrashIcon, PencilIcon, EyeIcon, CheckCircleIcon, SwatchIcon } from "@heroicons/react/24/outline"
import Tooltip from "aldoo-ra/Tooltip"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import ToggleSwitch from "aldoo-ra/ToggleSwitch"
import { twMerge } from "tailwind-merge"
import Tabs from "aldoo-ra/Tabs"
import DropdownSelect from "aldoo-ra/DropdownSelect"
import ThemePreviewEditor from "./ThemePreviewEditor"
import CMSColorTestSpecific from "./CMSColorTestSpecific"
import BrowserThemeAutoDetection from "./BrowserThemeAutoDetection"
import UserAvailableThemes from "./UserAvailableThemes"
import { GetCMSContent, SaveCMSContent, DeleteCMSContent } from "aldoo-ra/CMS/api"
import { GetParam } from "../ParamIO/api"
import { api } from "aldoo-ra/API"
import RestoreDialog from "aldoo-ra/CMS/dialogs/restore-dialog"
import TypeResolver from "aldoo-ra/CMS/type-resolver"
import Controller from "aldoo-ra/CMS/controllers/cms-controller"
import SiteTheme from "aldoo-ra/SiteTheme"
import LocalUserPreferences from "aldoo-ra/LocalUserPreferences"

const siteThemeAPI = api("site-theme")

export default function SiteThemes() {
  const [selectedInstance, setSelectedInstance] = useState(null)
  const [refreshKey, setRefreshKey] = useState(0)
  const [previewTheme, setPreviewTheme] = useState(null)
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)
  // Default to admin tab when on admin pages, site tab otherwise
  const [activeTab, setActiveTab] = useState(
    typeof window !== 'undefined' && window.location.pathname.startsWith('/admin') ? 'admin' : 'site'
  )
  const [allThemes, setAllThemes] = useState([]) // Store all themes for built-in logic
  const [preferenceMode, setPreferenceMode] = useState('site_settings') // 'my_preferences' or 'site_settings'
  const [preferencesInitialized, setPreferencesInitialized] = useState(false)
  const [openRestoreDialog, setOpenRestoreDialog] = useState(false)
  const [colorTestTheme, setColorTestTheme] = useState(null)
  const [isColorTestOpen, setIsColorTestOpen] = useState(false)
  const [colorTestCount, setColorTestCount] = useState(0)
  const [autoDetectionEnabled, setAutoDetectionEnabled] = useState(false)

  // Helper function to normalize isActive field to array format
  const normalizeIsActive = (isActive) => {
    if (Array.isArray(isActive)) {
      // Handle both string arrays ["site", "admin"] and object arrays [{value: "site", name: "Website"}]
      return isActive.map(item => {
        if (typeof item === "string") return item
        if (typeof item === "object" && item.value) return item.value
        return item
      }).filter(Boolean)
    }
    if (isActive === true) return ['site'] // Legacy boolean true = site active
    if (isActive === false) return [] // Legacy boolean false = inactive
    return [] // Any other value = inactive
  }

  const { getContentType } = TypeResolver()
  const siteThemeContentType = getContentType("SiteTheme")
  const controller = Controller()

  // Enhanced data fetcher that stores themes for built-in logic
  const fetchThemesWithState = async (params) => {
    try {
      const response = await GetCMSContent(params)

      if (response && response.data) {
        // Simple consistent sorting: built-in theme first, then by database order
        let sortedData = response.data.sort((a, b) => {
          const aBuiltIn = (a.content?.builtIn || false)
          const bBuiltIn = (b.content?.builtIn || false)
          if (aBuiltIn && !bBuiltIn) return -1
          if (!aBuiltIn && bBuiltIn) return 1
          // Maintain original database order for all other themes
          return 0
        })

        setAllThemes(sortedData) // Store in state for built-in logic

        return {
          ...response,
          data: sortedData
        }
      }

      return response
    } catch (error) {
      console.error('Error fetching themes:', error)
      return { data: [], totalItems: 0 }
    }
  }

  const hashColors = (colors) => {
    const str = JSON.stringify(colors.slice().sort((a, b) => a.key.localeCompare(b.key)))
    let hash = 5381
    for (let i = 0; i < str.length; i++) {
      hash = (((hash << 5) + hash) ^ str.charCodeAt(i)) >>> 0
    }
    return hash.toString(16)
  }

  const ensureDefaultTheme = async () => {
    try {
      const colors = []
      for (const sheet of document.styleSheets) {
        try {
          for (const rule of sheet.cssRules) {
            if (rule.selectorText === ":root") {
              const style = rule.style
              for (let i = 0; i < style.length; i++) {
                const prop = style[i]
                if (!prop.startsWith("--color-")) continue
                const key = prop.replace("--color-", "")
                const value = style.getPropertyValue(prop).trim()
                if (value) colors.push({ key, value })
              }
            }
          }
        } catch (_) {
          // Cross-origin stylesheet, skip
        }
      }

      if (colors.length === 0) return

      const colorsHash = hashColors(colors)
      const response = await siteThemeAPI({ ensure_default_theme: { colors, colorsHash } })

      if (response?.result?.created) {
        showNotification({ message: "Site Default theme created", className: "bg-admin_success text-white" })
        setRefreshKey((prev) => prev + 1)
      } else if (response?.result?.updated) {
        showNotification({ message: "Site Default theme updated", className: "bg-admin_success text-white" })
        setRefreshKey((prev) => prev + 1)
      }
    } catch (error) {
      // Non-critical - silent fail
    }
  }

  // Initialize SiteTheme API on component mount
  useEffect(() => {
    if (typeof window !== 'undefined' && SiteTheme) {
      SiteTheme.initialize().catch(error => {
        console.warn('SiteTheme initialization failed:', error)
      })
    }

    // Set up refresh handler for controller
    controller.onRefreshContents(() => setRefreshKey((prev) => prev + 1))

    // Load auto-detection status
    loadAutoDetectionStatus()

    // Ensure code-defined default theme exists and colors are up-to-date
    ensureDefaultTheme()
  }, [])

  // Load auto-detection status from ParamIO
  const loadAutoDetectionStatus = async () => {
    try {
      const autoDetection = await GetParam('SITE_THEME_AUTO_ADAPT').catch(() => null)
      setAutoDetectionEnabled(autoDetection?.value === true || autoDetection?.value === 'true')
    } catch (error) {
      console.warn('Failed to load auto-detection status:', error)
      setAutoDetectionEnabled(false)
    }
  }

  // Load preference mode from LocalUserPreferences (global, not per-tab)
  useEffect(() => {
    const savedPreferenceMode = LocalUserPreferences.get('ui.theme.preferenceMode')
    if (savedPreferenceMode) {
      setPreferenceMode(savedPreferenceMode)
    } else {
      setPreferenceMode('site_settings') // Default to site settings
    }
  }, []) // Remove activeTab dependency

  // Initialize user preferences from site settings if they don't exist
  useEffect(() => {
    const initializeUserPreferences = async () => {
      const sitePreferenceKey = 'ui.theme.site.selectedId'
      const adminPreferenceKey = 'ui.theme.admin.selectedId'

      const siteUserPref = LocalUserPreferences.get(sitePreferenceKey)
      const adminUserPref = LocalUserPreferences.get(adminPreferenceKey)

      // Only initialize if both are null/undefined
      if (!siteUserPref || !adminUserPref) {
        try {
          // Get all themes to find active ones
          const response = await GetCMSContent({
            match: {
              "content.type": "SiteTheme",
              deleted: { $ne: true }
            }
          })

          if (response.data && response.data.length > 0) {
            // Find active site theme
            if (!siteUserPref) {
              const activeSiteTheme = response.data.find(themeData => {
                const theme = themeData.content
                const normalizedIsActive = normalizeIsActive(theme.isActive)
                return normalizedIsActive.includes('site')
              })

              if (activeSiteTheme) {
                LocalUserPreferences.set(sitePreferenceKey, activeSiteTheme._id)
              }
            }

            // Find active admin theme
            if (!adminUserPref) {
              const activeAdminTheme = response.data.find(themeData => {
                const theme = themeData.content
                const normalizedIsActive = normalizeIsActive(theme.isActive)
                return normalizedIsActive.includes('admin')
              })

              if (activeAdminTheme) {
                LocalUserPreferences.set(adminPreferenceKey, activeAdminTheme._id)
              }
            }

            // Force table refresh to show the newly initialized toggles
            setRefreshKey((prev) => prev + 1)
          }

          // Mark preferences as initialized
          setPreferencesInitialized(true)
        } catch (error) {
          console.error('Failed to initialize user preferences from site settings:', error)
          setPreferencesInitialized(true) // Set as initialized even on error
        }
      } else {
        // Already have preferences, mark as initialized
        setPreferencesInitialized(true)
      }
    }

    // Only run if we have the required imports
    if (GetCMSContent && LocalUserPreferences) {
      initializeUserPreferences()
    }
  }, [])

  // Save preference mode to LocalUserPreferences when it changes (global)
  const handlePreferenceModeChange = async (newMode) => {
    // Extract the value from the object if it's an object, otherwise use as-is
    const modeValue = typeof newMode === 'object' ? newMode.value : newMode
    const previousMode = preferenceMode


    setPreferenceMode(modeValue)
    LocalUserPreferences.set('ui.theme.preferenceMode', modeValue)

    // Get current page context
    const currentPageContext = window.location.pathname.startsWith('/admin') ? 'admin' : 'site'

    // If switching to My Preferences mode, ensure user preferences are initialized
    if (modeValue === 'my_preferences') {
      const sitePreferenceKey = 'ui.theme.site.selectedId'
      const adminPreferenceKey = 'ui.theme.admin.selectedId'

      const siteUserPref = LocalUserPreferences.get(sitePreferenceKey)
      const adminUserPref = LocalUserPreferences.get(adminPreferenceKey)

      // Initialize preferences if they don't exist
      if (!siteUserPref || !adminUserPref) {
        try {

          // Get all themes to find active ones
          const response = await GetCMSContent({
            match: {
              "content.type": "SiteTheme",
              deleted: { $ne: true }
            }
          })

          if (response.data && response.data.length > 0) {
            // Find active site theme
            if (!siteUserPref) {
              const activeSiteTheme = response.data.find(themeData => {
                const theme = themeData.content
                const normalizedIsActive = normalizeIsActive(theme.isActive)
                return normalizedIsActive.includes('site')
              })

              if (activeSiteTheme) {
                LocalUserPreferences.set(sitePreferenceKey, activeSiteTheme._id)
              }
            }

            // Find active admin theme
            if (!adminUserPref) {
              const activeAdminTheme = response.data.find(themeData => {
                const theme = themeData.content
                const normalizedIsActive = normalizeIsActive(theme.isActive)
                return normalizedIsActive.includes('admin')
              })

              if (activeAdminTheme) {
                LocalUserPreferences.set(adminPreferenceKey, activeAdminTheme._id)
              }
            }
          }
        } catch (error) {
          console.error('Failed to initialize user preferences:', error)
        }
      }

      // Apply user preference for current page context when switching TO My Preferences mode
      const contextPreferenceKey = currentPageContext === 'admin' ? adminPreferenceKey : sitePreferenceKey
      const userPreferredThemeId = LocalUserPreferences.get(contextPreferenceKey)

      if (userPreferredThemeId && window.SiteTheme) {
        try {
          // Use SiteTheme API to apply the preferred theme
          await window.SiteTheme.setTheme(userPreferredThemeId, true, contextPreferenceKey)
        } catch (error) {
          console.warn('Failed to apply user preferred theme:', error)
        }
      }

      setRefreshKey((prev) => prev + 1)
    }

    // If switching from My Preferences to Site Settings, just refresh the table
    if (previousMode === 'my_preferences' && modeValue === 'site_settings') {
      // Don't change the applied theme, just update the UI to show site settings
      setRefreshKey((prev) => prev + 1)
    }
  }

  // Check if a theme is active based on current preference mode
  const isThemeActive = (theme, item = null) => {
    if (preferenceMode === 'my_preferences') {
      // In user preference mode, check LocalUserPreferences
      const preferenceKey = activeTab === 'admin' ? 'ui.theme.admin.selectedId' : 'ui.theme.site.selectedId'
      const userSelectedThemeId = LocalUserPreferences.get(preferenceKey)

      // The theme ID is at the item level (parent CMS document), not in the theme content
      // item is the full CMS document, theme is item.content
      const themeId = (item && item._id) || theme._id || theme.id

      return userSelectedThemeId === themeId
    } else {
      // In site settings mode, check CMS active state
      return normalizeIsActive(theme.isActive).includes(activeTab)
    }
  }

  // Get the name of currently selected theme for display in title
  const getCurrentThemeName = () => {
    if (preferenceMode === 'my_preferences') {
      // In My Preferences mode, get theme name from user preference
      const preferenceKey = activeTab === 'admin' ? 'ui.theme.admin.selectedId' : 'ui.theme.site.selectedId'
      const userSelectedThemeId = LocalUserPreferences.get(preferenceKey)

      if (userSelectedThemeId && allThemes.length > 0) {
        const selectedTheme = allThemes.find(themeData => themeData._id === userSelectedThemeId)
        return selectedTheme?.content?.name || 'Unknown Theme'
      }
    } else {
      // In Site Settings mode, get theme name from CMS active state
      if (allThemes.length > 0) {
        const activeTheme = allThemes.find(themeData => {
          const theme = themeData.content
          const normalizedIsActive = normalizeIsActive(theme.isActive)
          return normalizedIsActive.includes(activeTab)
        })
        return activeTheme?.content?.name || 'No Active Theme'
      }
    }
    return 'Loading...'
  }

  // Unified theme toggle handler that routes to user preferences or CMS based on preference mode
  const handleThemeToggle = async (item, isActive) => {
    const theme = item.content || item

    if (preferenceMode === 'my_preferences') {
      // User preferences mode - only save to LocalUserPreferences
      await handleUserPreferenceToggle(item, isActive)
    } else {
      // Site settings mode - save to CMS (existing behavior)
      if (theme.builtIn === true) {
        await handleBuiltInToggle(item, isActive)
      } else {
        await handleSetActiveToggle(item, isActive)
      }
    }
  }

  // Handle user preference toggle (LocalUserPreferences only)
  const handleUserPreferenceToggle = async (item, isActive) => {
    const theme = item.content || item
    const themeId = theme._id || item._id

    if (isActive) {
      // Set this theme as user's preference
      try {
        const preferenceKey = activeTab === 'admin' ? 'ui.theme.admin.selectedId' : 'ui.theme.site.selectedId'
        LocalUserPreferences.set(preferenceKey, themeId)

        // Determine if we should apply the theme immediately
        const currentPageContext = window.location.pathname.startsWith('/admin') ? 'admin' : 'site'
        const isCurrentContext = activeTab === currentPageContext

        // Only apply theme immediately if the activeTab matches the current page context
        // This prevents changing site theme while on admin pages from affecting admin appearance
        if (window.SiteTheme && window.SiteTheme.setTheme && isCurrentContext) {
          try {
            await window.SiteTheme.setTheme(themeId, true, preferenceKey)
          } catch (error) {
            console.warn('Failed to apply theme via SiteTheme.setTheme:', error)
          }
        }

        showNotification({
          message: isCurrentContext
            ? `"${theme.name}" applied and set as your personal ${activeTab} theme`
            : `"${theme.name}" set as your personal ${activeTab} theme (will apply when you visit ${activeTab} pages)`,
          className: "bg-admin_success text-white",
        })

        setRefreshKey((prev) => prev + 1)
      } catch (error) {
        console.error('Failed to set user preference:', error)
        showNotification({
          message: `Failed to set theme preference: ${error.message}`,
          className: "bg-admin_error text-white",
        })
      }
    } else {
      // Clear user preference
      const preferenceKey = activeTab === 'admin' ? 'ui.theme.admin.selectedId' : 'ui.theme.site.selectedId'
      LocalUserPreferences.delete(preferenceKey)

      showNotification({
        message: `Personal ${activeTab} theme preference cleared`,
        className: "bg-admin_success text-white",
      })

      setRefreshKey((prev) => prev + 1)
    }
  }

  const handleBuiltInToggle = async (item, isActive) => {
    const theme = item.content || item
    const currentContext = activeTab // 'site' or 'admin'

    if (isActive) {
      // Activating built-in theme - deactivate all other themes for current context
      await deactivateThemeForContext(currentContext)

      // Activate built-in theme for current context
      const currentIsActive = [...normalizeIsActive(theme.isActive)]
      if (!currentIsActive.includes(currentContext)) {
        currentIsActive.push(currentContext)
      }

      const updatedTheme = { ...theme, isActive: currentIsActive }

      // Update the built-in theme in CMS
      const itemWithUpdatedContent = {
        ...item,
        content: updatedTheme
      }

      const saveResult = await handleItemSave(itemWithUpdatedContent)
      if (!saveResult) {
        setRefreshKey((prev) => prev + 1)
        return
      }

      // Apply built-in theme only if user doesn't have personal preferences
      if (typeof window !== 'undefined' && window.SiteTheme) {
        // Check if current user has personal preferences for this context
        const preferenceKey = currentContext === 'admin' ? 'ui.theme.admin.selectedId' : 'ui.theme.site.selectedId'
        const userHasPersonalPreference = LocalUserPreferences.get(preferenceKey)


        if (!userHasPersonalPreference) {
          // Only apply if user doesn't have personal preferences
          if (window.SiteTheme.applyThemeToDOM) {
            window.SiteTheme.applyThemeToDOM(updatedTheme, currentContext)
          }

          showNotification({
            message: `Built-in theme activated for ${currentContext === 'site' ? 'website' : 'admin'} - using default CSS styles`,
            className: "bg-admin_success text-white",
          })
        } else {
          // User has personal preferences - don't override
          showNotification({
            message: `Built-in theme set for ${currentContext === 'site' ? 'website' : 'admin'} (your personal preference unchanged)`,
            className: "bg-admin_success text-white",
          })
        }
      }
    } else {
      // Cannot deactivate built-in theme directly - user must select another theme
      showNotification({
        message: `Please select another theme to replace the built-in ${currentContext === 'site' ? 'website' : 'admin'} theme`,
        className: "bg-admin_warning text-white",
      })
    }

    // Refresh the table to update toggle states
    setRefreshKey((prev) => prev + 1)
  }

  const handleItemDelete = (item) => {
    // Access theme data from content property
    const theme = item.content || item

    // Prevent deletion of built-in theme
    if (theme.builtIn === true) {
      showNotification({
        message: "Cannot delete the built-in theme",
        className: "bg-admin_warning text-white",
      })
      return
    }

    // Prevent deletion of active or default themes
    const isActiveAnywhere = normalizeIsActive(theme.isActive).length > 0

    if (isActiveAnywhere) {
      showNotification({
        message: "Cannot delete an active theme",
        className: "bg-admin_warning text-white",
      })
      return
    }

    // Different confirmation messages and behavior based on preference mode
    if (preferenceMode === 'my_preferences') {
      showPromptDialog({
        title: `Confirm Delete`,
        message: `Are you sure you want to delete theme "${theme.name}"?\n\nThis will permanently remove the theme for all users.`,
        confirmText: "Delete",
        onConfirm: async () => {
          const response = await DeleteCMSContent({ itemId: item._id, mode: "soft" })

          if (response.error) {
            showNotification({
              message: response.error,
              className: "bg-admin_error text-white",
            })
            return
          }

          showNotification({
            message: `Theme "${theme.name}" deleted successfully`,
            className: "bg-admin_success text-white",
          })
          setRefreshKey((prev) => prev + 1)
        },
      })
    } else {
      // Site settings mode - original behavior
      showPromptDialog({
        title: `Confirm Delete`,
        message: `Are you sure you want to delete theme "${theme.name}"?\n\nThis will permanently remove the theme for all users.`,
        confirmText: "Delete",
        onConfirm: async () => {
          const response = await DeleteCMSContent({ itemId: item._id, mode: "soft" })

          if (response.error) {
            showNotification({
              message: response.error,
              className: "bg-admin_error text-white",
            })
            return
          }

          showNotification({
            message: `Theme "${theme.name}" deleted successfully`,
            className: "bg-admin_success text-white",
          })
          setRefreshKey((prev) => prev + 1)
        },
      })
    }
  }

  const handleItemSave = async (item) => {
    // Get the actual theme content (might be wrapped or direct)
    const theme = item.content || item

    // If setting as active, show confirmation
    if (theme.isActive && selectedInstance && !(selectedInstance.content || selectedInstance).isActive) {
      const confirmed = await new Promise((resolve) => {
        showPromptDialog({
          title: "Set Active Theme",
          message: `Are you sure you want to set "${theme.name}" as the active theme? This will deactivate the current active theme.`,
          confirmText: "Set Active",
          onConfirm: () => resolve(true),
          onCancel: () => resolve(false),
        })
      })

      if (!confirmed) return false
    }

    // Check both the selectedInstance (for modal edits) and the item itself
    const itemId = selectedInstance?._id || item._id

    // Remove _id from content if it exists (as per Blog pattern)
    const updatedContent = { ...theme, type: "SiteTheme" }
    if (updatedContent._id) delete updatedContent._id

    // Use exact same pattern as Blog section - no extra type wrapper
    const savePayload = {
      payload: {
        ...(selectedInstance || item),  // Use selectedInstance if available, otherwise use item
        content: updatedContent,  // Don't wrap in extra type object
      }
    }

    // Call exactly like Blog section - no extra autoShowNotifications parameter
    const response = await controller.saveContent(savePayload)

    if (response.error) {
      showNotification({
        message: response.error,
        className: "bg-admin_error text-white",
      })
      return false
    }

    showNotification({
      message: `Theme ${!theme._id ? "created" : "updated"} successfully`,
      className: "bg-admin_success text-white",
    })
    setRefreshKey((prev) => prev + 1)

    // Apply theme in real-time if it's being set as active
    if (theme.isActive) {
      await applyThemeToPage(theme)
    }

    return true
  }


  const handleSetActiveToggle = async (item, isActive) => {
    const theme = item.content || item
    const currentContext = activeTab // 'site' or 'admin'

    if (isActive) {
      // Setting this theme as active for current context
      showPromptDialog({
        title: "Set Active Theme",
        message: `Are you sure you want to set "${theme.name}" as the active ${currentContext === 'site' ? 'website' : 'admin'} theme? This will deactivate the current active theme for this context.`,
        confirmText: "Set Active",
        onConfirm: async () => {
          // First deactivate current theme for this context
          await deactivateThemeForContext(currentContext)

          // Then activate this theme for current context
          const currentIsActive = [...normalizeIsActive(theme.isActive)]
          if (!currentIsActive.includes(currentContext)) {
            currentIsActive.push(currentContext)
          }

          const updatedTheme = { ...theme, isActive: currentIsActive }

          // Create a proper item structure that includes both _id and content
          const itemWithUpdatedContent = {
            ...item,  // This has _id, createdAt, etc.
            content: updatedTheme  // This has the updated theme data
          }

          // Call handleItemSave with the full item structure
          const saveResult = await handleItemSave(itemWithUpdatedContent)

          if (!saveResult) {
            // Still refresh to reset toggle state
            setRefreshKey((prev) => prev + 1)
            return
          }

          // Apply the theme based on context, but only if we're in the matching context
          const currentPageContext = window.location.pathname.startsWith('/admin') ? 'admin' : 'site'

          // In Site Settings mode, only apply theme to DOM if user doesn't have personal preferences
          if (window.SiteTheme && currentContext === currentPageContext) {
            // Check if current user has personal preferences for this context
            const preferenceKey = currentContext === 'admin' ? 'ui.theme.admin.selectedId' : 'ui.theme.site.selectedId'
            const userHasPersonalPreference = LocalUserPreferences.get(preferenceKey)


            if (!userHasPersonalPreference) {
              // Only apply if user doesn't have personal preferences
              try {
                if (window.SiteTheme.applyThemeToDOM) {
                  window.SiteTheme.applyThemeToDOM(updatedTheme, currentContext)
                }

                showNotification({
                  message: `Theme "${updatedTheme.name}" applied and set as active ${currentContext} theme for all users`,
                  className: "bg-admin_success text-white",
                })
              } catch (error) {
                console.warn('Failed to apply site-wide theme:', error)
              }
            } else {
              // User has personal preferences - don't override
              showNotification({
                message: `Site-wide ${currentContext} theme set to "${updatedTheme.name}" (your personal preference unchanged)`,
                className: "bg-admin_success text-white",
              })
            }
          } else {
            // Not in matching context, just show notification
            showNotification({
              message: `Site-wide ${currentContext} theme set to "${updatedTheme.name}"`,
              className: "bg-admin_success text-white",
            })
          }

          // Small delay to ensure database operations complete, then refresh
          setTimeout(() => {
            setRefreshKey((prev) => prev + 1)
            setSelectedInstance(null) // Clear selectedInstance
          }, 100)
        },
        onCancel: () => {
          // Refresh to reset the toggle state
          setRefreshKey((prev) => prev + 1)
        }
      })
    } else {
      // Prevent direct deselection - only allow switching to another theme
      showNotification({
        message: `Please select another theme to deactivate the current ${currentContext === 'site' ? 'website' : 'admin'} theme`,
        className: "bg-admin_warning text-white",
      })

      // Refresh to reset the toggle state back to active
      setRefreshKey((prev) => prev + 1)
    }
  }

  const deactivateThemeForContext = async (context) => {
    try {
      // Get all themes that are active for this context
      const response = await GetCMSContent({
        match: {
          "content.type": "SiteTheme",
          deleted: { $ne: true }
        }
      })

      if (response.data && response.data.length > 0) {
        // Process each theme to remove the context from isActive array
        for (const theme of response.data) {
          const themeContent = theme.content
          const currentIsActive = normalizeIsActive(themeContent.isActive)

          if (currentIsActive.includes(context)) {
            // Remove this context from the active array
            const newIsActive = currentIsActive.filter(ctx => ctx !== context)
            const updatedTheme = {
              ...themeContent,
              isActive: newIsActive
            }

            // Remove _id from content if it exists (as per Blog pattern)
            const updatedContent = {
              type: "SiteTheme",
              ...updatedTheme
            }
            if (updatedContent._id) delete updatedContent._id

            await controller.saveContent({
              payload: {
                ...theme,
                content: updatedContent
              }
            })
          }
        }
      }
    } catch (error) {
      console.error("Error deactivating themes for context:", error)
    }
  }


  const applyThemeToPage = async (theme) => {
    if (!theme || typeof window === 'undefined') return

    try {
      // Use the unified SiteTheme API for site-wide active theme setting
      if (window.SiteTheme && window.SiteTheme.setSiteActiveTheme) {
        const themeId = theme._id || theme.id
        if (themeId) {
          await window.SiteTheme.setSiteActiveTheme(themeId)
          showNotification({
            message: `Theme "${theme.name}" set as site-wide active theme`,
            className: "bg-admin_success text-white",
          })
        } else {
          console.warn('Cannot apply theme - missing ID:', theme)
        }
      } else {
        console.warn('SiteTheme API not available - falling back to direct application')

        // Fallback to direct application for backward compatibility
        const root = document.documentElement
        root.setAttribute('data-theme', theme.name?.toLowerCase().replace(/\s+/g, '-') || 'custom')

        const themeColors = theme.colors || {}
        Object.entries(themeColors).forEach(([colorKey, colorValue]) => {
          if (colorValue) {
            root.style.setProperty(`--color-${colorKey}`, colorValue)
          }
        })

        showNotification({
          message: `Theme "${theme.name}" applied (fallback mode)`,
          className: "bg-admin_success text-white",
        })
      }
    } catch (error) {
      console.error('Failed to apply theme:', error)
      showNotification({
        message: `Failed to apply theme: ${error.message}`,
        className: "bg-admin_error text-white",
      })
    }
  }

  const handlePreviewTheme = (item) => {
    const theme = item.content || item
    
    // Ensure the theme has an ID for preview functionality
    const themeWithId = {
      ...theme,
      _id: item._id || theme._id || theme.id
    }

    if (theme.builtIn === true) {
      // For built-in theme, create a special preview that resets data-theme
      setPreviewTheme({
        ...themeWithId,
        isBuiltIn: true // Flag for preview component
      })
    } else {
      setPreviewTheme(themeWithId)
    }

    setIsPreviewOpen(true)
  }

  const handleColorTest = (item) => {
    const theme = item.content || item
    const themeWithId = {
      ...theme,
      _id: item._id || theme._id // Ensure we have the ID
    }
    setColorTestTheme(themeWithId)
    setColorTestCount(0)
    setIsColorTestOpen(true)
  }

  const themeItemRender = [
    {
      id: "active",
      title: "Active",
      headerClassName: "w-1/8 text-center",
      render: (_, item) => {
        const theme = item.content || item
        // Check if theme is active based on preference mode
        const isActiveForCurrentTab = isThemeActive(theme, item)
        const isBuiltIn = theme.builtIn === true

        // Show "Auto detect" when auto-detection is enabled for Website tab
        if (autoDetectionEnabled && activeTab === 'site') {
          return (
            <div className="flex justify-center">
              <span className="px-2 py-1 text-xs bg-admin_primary text-white rounded">
                Auto detect
              </span>
            </div>
          )
        }

        // Built-in theme has special handling but uses normal toggle logic
        if (isBuiltIn) {
          return (
            <div className="flex justify-center">
              <ToggleSwitch
                variant="admin"
                isOn={isActiveForCurrentTab}
                onChange={(isActive) => handleThemeToggle(item, isActive)}
                size="sm"
              />
            </div>
          )
        }

        return (
          <div className="flex justify-center">
            <ToggleSwitch
              variant="admin"
              isOn={isActiveForCurrentTab}
              onChange={(isActive) => handleThemeToggle(item, isActive)}
              size="sm"
            />
          </div>
        )
      },
    },
    {
      id: "name",
      title: "Theme Name",
      headerClassName: "w-1/4",
      render: (_, item) => {
        const theme = item.content || item
        const isActiveForCurrentTab = isThemeActive(theme, item)

        return (
          <div className="flex flex-col">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-admin_text">{theme.name}</span>
              {isActiveForCurrentTab && (
                <CheckCircleIcon className="w-4 h-4 text-admin_success" />
              )}
              {theme.isDefault && (
                <span className="px-2 py-1 text-xs bg-admin_neutral_1 text-admin_text rounded">
                  DEFAULT
                </span>
              )}
            </div>
            {theme.description && (
              <span className="text-sm text-admin_text_secondary">
                {theme.description}
              </span>
            )}
          </div>
        )
      },
    },
    {
      id: "colors",
      title: "Color Preview",
      headerClassName: "w-1/4",
      render: (_, item) => {
        const theme = item.content || item
        const colors = theme.colors || {}

        // Show primary colors as preview
        const previewColors = [
          { name: 'Primary', key: 'primary' },
          { name: 'Secondary', key: 'secondary' },
          { name: 'Success', key: 'success' },
          { name: 'Error', key: 'error' }
        ]

        const getColor = (key) => {
          if (colors[key]) return `rgb(${colors[key]})`
          // Fall back to CSS variable for themes without stored colors (e.g. Site Default)
          if (typeof window !== 'undefined') {
            const cssVar = getComputedStyle(document.documentElement).getPropertyValue(`--color-${key}`).trim()
            if (cssVar) return `rgb(${cssVar})`
          }
          return undefined
        }

        return (
          <div className="flex items-center gap-1">
            {previewColors.map(({ name, key }) => (
              <div
                key={key}
                className="w-4 h-4 rounded border border-admin_bordered"
                style={{ backgroundColor: getColor(key) }}
                title={name}
              />
            ))}
          </div>
        )
      },
    },
    {
      id: "status",
      title: "Status",
      headerClassName: "w-1/6",
      render: (_, item) => {
        const theme = item.content || item
        const isActiveForCurrentTab = isThemeActive(theme, item)

        return (
          <div className="flex flex-col gap-1">
            <span className={twMerge(
              "px-2 py-1 rounded text-xs font-medium",
              isActiveForCurrentTab
                ? "bg-admin_success text-white"
                : "bg-admin_neutral_2 text-admin_text"
            )}>
              {isActiveForCurrentTab ? "ACTIVE" : "INACTIVE"}
            </span>
            <span className="text-xs text-admin_text_secondary">
              v{theme.version || "1.0.0"}
            </span>
          </div>
        )
      },
    },
    {
      id: "actions",
      title: "Actions",
      headerClassName: "w-1/4 text-center",
      render: (_, item) => {
        const theme = item.content || item
        const isActiveAnywhere = normalizeIsActive(theme.isActive).length > 0
        const isBuiltIn = theme.builtIn === true
        const isSiteDefault = theme.themeID === "siteDefault"

        return (
          <div className="flex items-center justify-center gap-1">
            <Tooltip content="Preview Theme">
              <Button
                variant="admin_icon"
                onClick={() => handlePreviewTheme(item)}
              >
                <EyeIcon className="w-4 h-4" />
              </Button>
            </Tooltip>

            <Tooltip content="Test Colors">
              <Button
                variant="admin_icon"
                onClick={() => handleColorTest(item)}
              >
                <SwatchIcon className="w-4 h-4" />
              </Button>
            </Tooltip>

            {!isBuiltIn && !isSiteDefault && (
              <>
                <Tooltip content="Edit Theme">
                  <Button
                    variant="admin_icon"
                    onClick={() => setSelectedInstance(item)}
                  >
                    <PencilIcon className="w-4 h-4" />
                  </Button>
                </Tooltip>

                <Tooltip content="Delete Theme">
                  <Button
                    variant="admin_icon"
                    onClick={() => handleItemDelete(item)}
                    disabled={isActiveAnywhere}
                    className={isActiveAnywhere ? "opacity-50 cursor-not-allowed" : ""}
                  >
                    <TrashIcon className="w-4 h-4" />
                  </Button>
                </Tooltip>
              </>
            )}
          </div>
        )
      },
    },
  ]

  const headerControls = (
    <div className="w-full flex flex-row items-center justify-between gap-5 mt-5">
      <div className="flex items-center gap-3">
      </div>
    </div>
  )

  return (
    <div className="p-4">
      {/* Theme Management Controls */}
      <div className="mb-6 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <Typography 
            component="h1"
            variant="admin_h3" 
          >
            Themes - {preferenceMode === 'my_preferences' ? 'Your Browser' : 'All Users'}
          </Typography>
        </div>

        <div className="flex items-center gap-3">
          <DropdownSelect
            key={`preference-mode-${preferenceMode}`}
            variant="admin"
            value={[{
              value: preferenceMode,
              name: preferenceMode === 'my_preferences' ? 'Your Browser' : 'All Users'
            }]}
            onChange={(selected) => handlePreferenceModeChange(selected[0])}
            options={[
              { value: 'site_settings', name: 'All Users' },
              { value: 'my_preferences', name: 'Your Browser' }
            ]}
            className="min-w-[180px]"
            multiSelection={false}
            showCheckbox={false}
            showSelectionTick={true}
            enableResetButton={false}
          />

          <Button
            variant="admin_secondary"
            className="whitespace-nowrap"
            onClick={() => setOpenRestoreDialog(true)}
            style={{
              opacity: preferenceMode === 'site_settings' ? 1 : 0.5,
              pointerEvents: preferenceMode === 'site_settings' ? 'auto' : 'none'
            }}
          >
            Restore
          </Button>

          <Button
            variant="admin_primary"
            className="whitespace-nowrap gap-1"
            onClick={() => setSelectedInstance({
              name: "",
              description: "",
              version: "1.0.0",
              isActive: false,
              isDefault: false
            })}
            style={{
              opacity: preferenceMode === 'site_settings' ? 1 : 0.5,
              pointerEvents: preferenceMode === 'site_settings' ? 'auto' : 'none'
            }}
          >
            <PlusCircleIcon className="w-5 h-5" />
            Create Theme
          </Button>
        </div>
      </div>

      {/* Website/Admin Context Tabs */}
      <div className="mb-6">
        <Tabs
          tabs={[
            { value: 'site', label: 'Website' },
            { value: 'admin', label: 'Admin' }
          ]}
          value={activeTab}
          onChange={setActiveTab}
          variant="admin"
        />
      </div>

      <div className="mb-6">
        <DataTable
          variant="admin_browser"
          title={`${activeTab === 'site' ? 'Website' : 'Admin'} - ${getCurrentThemeName()}`}
          preferredHeight="70vh"
          apiMethod={fetchThemesWithState}
          headerControls={headerControls}
          itemHeight="h-[60px]"
          searchField="content.name content.description"
          searchObject={{}}
          requestFields={{
            match: {
              "content.type": "SiteTheme",
              deleted: { $ne: true }
            },
          }}
          sort={{ createdAt: -1 }}
          renders={themeItemRender}
          refreshKey={refreshKey}
          itemsPerPage={10}
          localUserPreferencesKey="ui.siteThemes"
          styleOverrides={{
            table: "w-full table-fixed",
            container: "relative w-full flex flex-col h-[70vh] justify-start bg-admin_paper rounded-3xl p-6 pb-4",
            searchInput: "absolute top-4 right-4 text-text bg-transparent outline-none flex-1 w-[350px] h-[30px] px-2 py-1",
          }}
          noData={
            <Typography variant="admin_body">
              No themes created yet
            </Typography>
          }
        />
      </div>

      {/* User Available Themes Panel */}
      <UserAvailableThemes key={refreshKey} />

      {/* Browser Theme Auto-Detection Panel */}
      <BrowserThemeAutoDetection key={refreshKey} onSettingsChange={loadAutoDetectionStatus} />

      {/* Theme Editor Modal */}
      <Modal
        isOpen={selectedInstance !== null}
        onClose={() => setSelectedInstance(null)}
        variant="admin"
        className="w-full max-w-[95%] max-h-[90%]"
      >
        <ContentEditor
          title={selectedInstance?._id ? "Edit Theme" : "Create Theme"}
          contentType={siteThemeContentType && (() => {
            const isSiteDefault = (selectedInstance?.content?.themeID || selectedInstance?.themeID) === "siteDefault"
            return {
              ...siteThemeContentType,
              fields: siteThemeContentType.fields?.map(field =>
                field.id === "colors" && isSiteDefault
                  ? { ...field, readOnly: true }
                  : field
              ),
              groups: siteThemeContentType.groups?.map(group => {
                if (group.id === "basic_information") {
                  return { ...group, expanded: !selectedInstance?._id }
                } else if (group.id === "colors") {
                  return { ...group, expanded: true }
                } else {
                  return group
                }
              })
            }
          })()}
          dataItem={selectedInstance}
          fieldValues={(selectedInstance && selectedInstance.content) || selectedInstance || {}}
          onError={(errors) => {
            showNotification({
              message: Object.values(errors).find((e) => e),
              className: "bg-admin_error text-white",
            })
          }}
          onSave={handleItemSave}
          onClickSave={(result) => {
            if (!result) return
            setSelectedInstance(null)
          }}
        />
      </Modal>

      {/* Theme Preview Modal */}
      {isPreviewOpen && (
        <ThemePreviewEditor
          theme={previewTheme}
          allThemes={allThemes}
          onClose={() => setIsPreviewOpen(false)}
          onThemeChange={(newTheme) => {
            setPreviewTheme(newTheme)
          }}
        />
      )}

      {/* Color Test Modal */}
      <Modal
        variant="fullScreen"
        isOpen={isColorTestOpen}
        onClose={() => setIsColorTestOpen(false)}
        title={`Color Test - ${colorTestTheme?.name || 'Theme'} (v${colorTestTheme?.version || '1.0.0'}) - ${colorTestCount} colors`}
      >
        {colorTestTheme && (
          <CMSColorTestSpecific theme={colorTestTheme} onColorsLoaded={setColorTestCount} />
        )}
      </Modal>


      {/* Restore Dialog */}
      <RestoreDialog
        isOpen={openRestoreDialog}
        onClose={() => setOpenRestoreDialog(false)}
        type="content"
        controller={controller}
        additionalFilters={{
          "content.type": "SiteTheme",
          deleted: true
        }}
      />
    </div>
  )
}